import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { KestrelLoadingWidget } from 'features/kestrelViewer'
import { PixelStreamUI } from 'features/kestrelViewer/PixelStreamUI'
import { KestrelTutorialProvider } from 'features/kestrelViewer/tutorial/KestrelTutorialContext'
import React, { FC, useCallback, useEffect } from 'react'
import styled from 'styled-components'

import { useKestrel } from './KestrelProvider'

const LOADING_DISPLAY_TIMEOUT_MS = 2 * 60 * 1000 // 2 minutes
const BLUR_ACTIVATION_DELAY_MS = 3 * 60 * 1000 // 3 minutes
const VM_SHUTDOWN_DELAY_MS = 60 * 60 * 1000 // 1 hour
const RESERVE_TIMEOUT_MS = 30 * 1000
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAYS = [4000, 8000, 16000]

const BlurOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(0.5rem);
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 9999;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  font-weight: 600;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
`

// Component that handles getting a pixel streaming url to connect to
export const KestrelUI: FC<{ kestrelModelId: string }> = ({
  kestrelModelId,
}) => {
  /*
        This Class handles the negotiation to the Kestrel Matchmaker service to retrieve a
        valid Pixel Streaming URL

        If the matchmaker service returns a valid URL to stream the Kestrel scene, it will
        initialize the PixelStreamUI component. If not, it will display a message to the user.
    */

  const { authorization } = useGeckoAuth()
  const { KESTREL_MATCHMAKER_URL } = useRuntimeVariables()
  const { IS_PROD } = useEnvironmentVariables()
  const {
    pixelStreamingURL,
    setPixelStreamingURL,
    kestrelStatus,
    setLoading,
    setError,
    setHasTimedOut,
    setBlurVisible,
    setVmID,
    setVmDeleted,
    startLoadingSequence,
    setLoadingStep,
  } = useKestrel()
  const reserveURL = `${KESTREL_MATCHMAKER_URL}/api/v1/kestrel/reserve`
  const deleteURL = `${KESTREL_MATCHMAKER_URL}/api/v1/kestrel`
  const {
    isLoading,
    error,
    hasTimedOut,
    showBlur,
    vmID,
    isVmDeleted,
    isResetting,
  } = kestrelStatus

  const fetchWithTimeout = useCallback(
    async (url: string, options: RequestInit, timeout: number) => {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), timeout)

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        })
        clearTimeout(timeoutId)
        return response
      } catch (error) {
        clearTimeout(timeoutId)
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Request timed out. Please try again.')
        }
        throw error
      }
    },
    []
  )

  const parseErrorResponse = useCallback(
    async (
      response: Response
    ): Promise<{ message: string; isRetryable: boolean }> => {
      const raw = (await response.text()) ?? ''
      let detail = ''

      try {
        const json = raw ? JSON.parse(raw) : null
        if (json?.detail) {
          detail = String(json.detail).trim()
        }
      } catch {
        // ignore JSON parse errors; fall back to raw text below
      }

      const status = response.status

      switch (status) {
        case 503:
          return {
            message: 'No servers available. Retrying...',
            isRetryable: true,
          }
        case 409:
          return {
            message: 'Server busy. Retrying...',
            isRetryable: true,
          }
        case 500:
          return {
            message: 'Server error. Retrying...',
            isRetryable: true,
          }
        case 400:
          if (detail.includes('concurrent models')) {
            return {
              message:
                'You have reached the maximum of 6 concurrent models. Please close another model and try again.',
              isRetryable: false,
            }
          }
          return {
            message: detail || 'Bad request. Please try again.',
            isRetryable: false,
          }
        case 404:
          if (detail.includes('Asset executable')) {
            return {
              message: 'Model files are missing. Please contact support.',
              isRetryable: false,
            }
          }
          return {
            message: 'This model could not be found. It may have been deleted.',
            isRetryable: false,
          }
        case 403:
          return {
            message: "You don't have permission to access this model.",
            isRetryable: false,
          }
        case 401:
          return {
            message:
              'Authentication failed. Please reload the page and try again.',
            isRetryable: false,
          }
        case 422:
          return {
            message: 'Invalid request. Please reload the page and try again.',
            isRetryable: false,
          }
        default:
          return {
            message:
              detail ||
              `Failed to connect to matchmaker service: ${status} ${response.statusText}`,
            isRetryable: false,
          }
      }
    },
    []
  )

  const fetchStreamingURL = useCallback(async () => {
    setLoading(true)
    setError(null)
    setHasTimedOut(false)
    setVmDeleted(false)
    const sequence = startLoadingSequence()
    setLoadingStep('reserving-vm', sequence)

    let success = false
    let finalError: string | null = null

    for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt += 1) {
      try {
        const response = await fetchWithTimeout(
          reserveURL,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${authorization}`,
            },
            body: JSON.stringify({ kestrelModelId }),
          },
          RESERVE_TIMEOUT_MS
        )

        if (!response.ok) {
          const { message, isRetryable } = await parseErrorResponse(response)

          if (!isRetryable || attempt === MAX_RETRY_ATTEMPTS - 1) {
            throw new Error(message)
          }

          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAYS[attempt] ?? RETRY_DELAYS[0])
          )
          continue
        }

        const rawUrl = (await response.text()).trim().replace(/"/g, '')
        if (!rawUrl) {
          throw new Error('Empty streaming URL received')
        }

        const websocketUrl = rawUrl.startsWith('wss://')
          ? rawUrl
          : `wss://${rawUrl}`

        setLoadingStep('connecting', sequence)
        setPixelStreamingURL(websocketUrl)

        const vmIdMatch = rawUrl
          .toLowerCase()
          .match(
            /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/
          )
        if (vmIdMatch && vmIdMatch[1]) {
          setVmID(vmIdMatch[1])
        } else {
          setVmID(null)
        }

        success = true
        break
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error'
        finalError = message

        if (attempt === MAX_RETRY_ATTEMPTS - 1) {
          break
        }

        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAYS[attempt] ?? RETRY_DELAYS[0])
        )
      }
    }

    if (!success) {
      setError(finalError ?? 'Unable to reserve a Kestrel VM right now.')
      setLoading(false)
      return
    }

    setLoading(false)
  }, [
    authorization,
    reserveURL,
    kestrelModelId,
    setPixelStreamingURL,
    setLoading,
    setError,
    setHasTimedOut,
    setVmDeleted,
    setVmID,
    setLoadingStep,
    startLoadingSequence,
    fetchWithTimeout,
    parseErrorResponse,
  ])

  useEffect(() => {
    if (!authorization || !reserveURL || !kestrelModelId) {
      return
    }

    if (!pixelStreamingURL && !showBlur) {
      void fetchStreamingURL()
    }
  }, [
    authorization,
    kestrelModelId,
    reserveURL,
    pixelStreamingURL,
    showBlur,
    fetchStreamingURL,
  ])

  useEffect(() => {
    // Don't set timeout if no URL or if video is already ready
    if (!pixelStreamingURL || kestrelStatus?.videoReady) {
      // Reset hasTimedOut if video is ready
      if (kestrelStatus?.videoReady && hasTimedOut) {
        setHasTimedOut(false)
      }
      return
    }

    const timeout = setTimeout(() => {
      setHasTimedOut(true)
    }, LOADING_DISPLAY_TIMEOUT_MS)

    return () => {
      clearTimeout(timeout)
    }
  }, [
    pixelStreamingURL,
    kestrelStatus?.videoReady,
    hasTimedOut,
    setHasTimedOut,
  ])

  const deleteVM = useCallback(async () => {
    if (!authorization || !deleteURL || !vmID) {
      return
    }

    try {
      const response = await fetch(`${deleteURL}/${vmID}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${authorization}`,
        },
      })

      if (!response.ok) {
        console.error(`Failed to delete VM: ${response.statusText}`)
      } else {
        setVmDeleted(true)
        setPixelStreamingURL('')
      }
    } catch (error) {
      console.error('Error deleting VM:', error)
    }
  }, [authorization, deleteURL, vmID, setPixelStreamingURL, setVmDeleted])

  useEffect(() => {
    if (!vmID) return

    let blurTimeout: NodeJS.Timeout | null = null
    let vmShutdownTimeout: NodeJS.Timeout | null = null

    const clearAllTimeouts = () => {
      if (blurTimeout) {
        clearTimeout(blurTimeout)
        blurTimeout = null
      }
      if (vmShutdownTimeout) {
        clearTimeout(vmShutdownTimeout)
        vmShutdownTimeout = null
      }
    }

    const handleUserAway = () => {
      if (isVmDeleted) return

      clearAllTimeouts()

      blurTimeout = setTimeout(() => {
        setBlurVisible(true)
      }, BLUR_ACTIVATION_DELAY_MS)

      vmShutdownTimeout = setTimeout(() => {
        deleteVM()
      }, VM_SHUTDOWN_DELAY_MS)
    }

    const handleUserReturn = () => {
      clearAllTimeouts()
      setBlurVisible(false)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleUserAway()
      } else {
        handleUserReturn()
      }
    }

    window.addEventListener('blur', handleUserAway)
    window.addEventListener('focus', handleUserReturn)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearAllTimeouts()
      window.removeEventListener('blur', handleUserAway)
      window.removeEventListener('focus', handleUserReturn)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [vmID, isVmDeleted, deleteVM, fetchStreamingURL, setBlurVisible])

  // Show loading until both VM is connected and websocket stream is ready, or until timeout
  const showLoading =
    isLoading ||
    (pixelStreamingURL && !kestrelStatus?.videoReady && !hasTimedOut)

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
      }}>
      <BlurOverlay $isVisible={showBlur}>Click to Resume</BlurOverlay>
      <KestrelLoadingWidget
        showLoading={Boolean(showLoading)}
        hasError={Boolean(error)}
        currentStep={kestrelStatus.loadingStep}
        showStepLabel={!IS_PROD}
      />
      {(!isLoading && pixelStreamingURL) || isResetting ? (
        <KestrelTutorialProvider>
          <PixelStreamUI />
        </KestrelTutorialProvider>
      ) : null}
      {!isLoading && !pixelStreamingURL && !isResetting && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: '#ff4444',
            textAlign: 'center',
            padding: '20px',
          }}>
          <p>{error || 'No pixel streaming URL found'}</p>
        </div>
      )}
    </div>
  )
}
